import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Plus, Pencil, Trash2, GripVertical, Star, ExternalLink, FolderKanban } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";

import {
  PageHeader,
  Button,
  IconButton,
  Tooltip,
  Spinner,
  EmptyState,
  ProjectStatusBadge,
  ProjectCover,
} from "../../design-system";

/* -------------------------------------------------------------------------- */
/*  Ligne triable                                                             */
/* -------------------------------------------------------------------------- */

function SortableProjectRow({ project, onDelete, onToggleFeatured }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const stackCount =
    (project.technologies?.length || 0) +
    (project.frameworks?.length || 0) +
    (project.databases?.length || 0) +
    (project.tools?.length || 0);

  return (
    <tr ref={setNodeRef} style={style} {...attributes} className="hover:bg-bg-2/50 transition-colors">
      <td className="w-10 px-3 py-3 align-middle">
        <button
          type="button"
          {...listeners}
          aria-label="Glisser pour réordonner"
          className="cursor-grab active:cursor-grabbing p-1 text-mute hover:text-text"
        >
          <GripVertical className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </td>
      <td className="w-16 px-3 py-2 align-middle">
        <ProjectCover
          title={project.title}
          slug={project.slug}
          src={project.thumbUrl || project.coverUrl}
          accentColor={project.accentColor}
          ratio="square"
          overlay="none"
          rounded="rounded-md"
          className="w-11"
        />
      </td>
      <td className="px-3 py-3 align-middle">
        <div className="font-semibold text-text leading-tight">{project.title}</div>
        {project.tagline && (
          <div className="text-xs text-mute mt-0.5 line-clamp-1">{project.tagline}</div>
        )}
      </td>
      <td className="px-3 py-3 align-middle">
        <ProjectStatusBadge status={project.status} size="sm" />
      </td>
      <td className="px-3 py-3 align-middle text-sm text-mute font-mono">{project.period || "—"}</td>
      <td className="px-3 py-3 align-middle text-sm font-mono tabular-nums text-mute w-20 text-right">
        {stackCount || "—"}
      </td>
      <td className="px-3 py-3 align-middle w-12 text-center">
        <Tooltip content={project.featured ? "Retirer de l'accueil" : "Mettre en avant sur l'accueil"}>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={project.featured ? "Retirer de l'accueil" : "Mettre en avant sur l'accueil"}
            aria-pressed={project.featured}
            onClick={() => onToggleFeatured(project)}
            icon={
              <Star
                strokeWidth={1.8}
                className={project.featured ? "fill-current text-ink" : "text-mute-2"}
              />
            }
          />
        </Tooltip>
      </td>
      <td className="px-3 py-3 align-middle w-32">
        <div className="flex items-center gap-0.5 justify-end">
          <Tooltip content="Voir la page publique">
            <IconButton
              as="a"
              href={`/projets/${project.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="ghost"
              icon={<ExternalLink strokeWidth={1.8} />}
              aria-label={`Voir la page de ${project.title}`}
            />
          </Tooltip>
          <Tooltip content="Modifier">
            <IconButton
              as={Link}
              to={`/dashboard/projects/${project.id}`}
              size="sm"
              variant="ghost"
              icon={<Pencil strokeWidth={1.8} />}
              aria-label={`Modifier ${project.title}`}
            />
          </Tooltip>
          <Tooltip content="Supprimer">
            <IconButton
              size="sm"
              variant="ghost"
              icon={<Trash2 strokeWidth={1.8} />}
              aria-label={`Supprimer ${project.title}`}
              onClick={() => onDelete(project)}
            />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function SortableProjectTable({ projects, onDragEnd, onDelete, onToggleFeatured }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <div className="rounded-lg bg-cream overflow-hidden border border-ink/12 overflow-x-auto">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table className="w-full text-sm border-collapse">
          <thead className="bg-ink text-cream">
            <tr>
              <th className="w-10 px-3 py-2.5"></th>
              <th className="w-16 px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Projet</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Statut</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Période</th>
              <th className="text-right px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold w-20">Stack</th>
              <th className="text-center px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold w-12">Une</th>
              <th className="w-32 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/8">
            <SortableContext items={projects.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              {projects.map((project) => (
                <SortableProjectRow
                  key={project.id}
                  project={project}
                  onDelete={onDelete}
                  onToggleFeatured={onToggleFeatured}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Écran                                                                     */
/* -------------------------------------------------------------------------- */

const notify = (title, message, type = "success", duration = 3000) =>
  Store.addNotification({
    title,
    message,
    type,
    insert: "top",
    container: "top-right",
    dismiss: { duration },
  });

class ProjectList extends Component {
  constructor(props) {
    super(props);
    this.state = { projects: [], loading: true };
  }

  componentDidMount() {
    this.loadProjects();
  }

  withAuth = () => {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
  };

  loadProjects = async () => {
    try {
      this.withAuth();
      const response = await axios.get("/api/projects?limit=200");
      this.setState({ projects: response.data.projects || [], loading: false });
    } catch (error) {
      console.error("Error loading projects:", error);
      this.setState({ loading: false });
      notify("Erreur", "Impossible de charger les projets", "danger");
    }
  };

  handleToggleFeatured = async (project) => {
    const nextFeatured = !project.featured;

    // Optimiste : l'étoile réagit tout de suite, on resynchronise en cas d'échec.
    this.setState((state) => ({
      projects: state.projects.map((p) =>
        p.id === project.id ? { ...p, featured: nextFeatured } : p,
      ),
    }));

    try {
      this.withAuth();
      await axios.put(`/api/projects/${project.id}`, { featured: nextFeatured });
    } catch (error) {
      console.error("Error toggling featured:", error);
      this.loadProjects();
      notify("Erreur", "Impossible de modifier la mise en avant", "danger");
    }
  };

  handleDelete = (project) => {
    confirmAlert({
      title: "Supprimer ce projet ?",
      message: `« ${project.title} » et sa page publique seront supprimés définitivement.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          this.withAuth();
          await axios.delete(`/api/projects/${project.id}`);
          notify("Projet supprimé", `« ${project.title} » a été retiré.`);
          this.loadProjects();
        } catch (error) {
          console.error("Error deleting project:", error);
          notify("Erreur", "Impossible de supprimer le projet", "danger");
        }
      },
    });
  };

  handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = this.state.projects.findIndex((p) => p.id === active.id);
    const newIndex = this.state.projects.findIndex((p) => p.id === over.id);
    const newProjects = arrayMove(this.state.projects, oldIndex, newIndex);
    this.setState({ projects: newProjects });

    try {
      this.withAuth();
      const orderList = newProjects.map((project, index) => ({ id: project.id, order: index }));
      await axios.put("/api/projects/reorder", { orderList });
      this.setState({ projects: newProjects.map((p, i) => ({ ...p, order: i })) });
      notify("Ordre mis à jour", null, "success", 2000);
    } catch (error) {
      console.error("Error reordering projects:", error);
      this.loadProjects();
      notify("Erreur", "Impossible de mettre à jour l'ordre", "danger");
    }
  };

  render() {
    const { projects, loading } = this.state;
    const featuredCount = projects.filter((p) => p.featured).length;

    return (
      <AdminPageShell pageTitle="Projets" padded={false}>
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section iv · Projets"
            title={<>Les réalisations, <span className="font-serif italic font-normal align-baseline">en vitrine.</span></>}
            description={
              projects.length > 0
                ? `${projects.length} projet${projects.length > 1 ? "s" : ""} · ${featuredCount} sur l'accueil. L'ordre de cette liste est celui du site.`
                : "L'ordre de cette liste est celui du site. L'étoile pousse un projet sur l'accueil."
            }
            actions={
              <Button
                as={Link}
                to="/dashboard/projects/new"
                leftIcon={<Plus className="w-4 h-4" strokeWidth={1.8} />}
              >
                Nouveau projet
              </Button>
            }
          />

          {loading ? (
            <div className="flex items-center justify-center py-20 text-mute">
              <Spinner size="lg" />
            </div>
          ) : projects.length === 0 ? (
            <EmptyState
              icon={<FolderKanban className="w-6 h-6" strokeWidth={1.5} />}
              title="Aucun projet pour le moment"
              description="Crée ta première page projet : contexte, stack, captures et liens."
              action={
                <Button as={Link} to="/dashboard/projects/new" leftIcon={<Plus className="w-4 h-4" />}>
                  Nouveau projet
                </Button>
              }
            />
          ) : (
            <SortableProjectTable
              projects={projects}
              onDragEnd={this.handleDragEnd}
              onDelete={this.handleDelete}
              onToggleFeatured={this.handleToggleFeatured}
            />
          )}
        </div>
      </AdminPageShell>
    );
  }
}

export default withRouter(ProjectList);
